import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, Brain, Database, Zap, Search } from 'lucide-react';
import { api } from '../../services/api';

interface EntityUploadTabProps {
    entityId: string;
    projectId: string;
}

interface PipelineStep {
    step: string;
    detail: string;
    ts: string;
}

const STEP_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    queued:          { label: 'En cola',             icon: Loader2,      color: 'text-slate-400' },
    extracting:      { label: 'Extrayendo texto',    icon: FileText,     color: 'text-blue-400' },
    chunking:        { label: 'Chunking semántico',  icon: Brain,        color: 'text-amber-400' },
    embedding:       { label: 'Generando embeddings',icon: Zap,          color: 'text-purple-400' },
    indexing:        { label: 'Indexando en Qdrant',  icon: Database,     color: 'text-cyan-400' },
    completed:       { label: 'Completado',          icon: CheckCircle2, color: 'text-green-400' },
    post_processing: { label: 'Post-procesamiento',  icon: Search,       color: 'text-indigo-400' },
    done:            { label: 'Pipeline completo',   icon: CheckCircle2, color: 'text-green-400' },
    error:           { label: 'Error',               icon: AlertCircle,  color: 'text-red-400' },
};

const EntityUploadTab: React.FC<EntityUploadTabProps> = ({ entityId, projectId }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [runId, setRunId] = useState<string | null>(null);
    const [steps, setSteps] = useState<PipelineStep[]>([]);
    const [currentStep, setCurrentStep] = useState<string>('');
    const [pipelineDone, setPipelineDone] = useState(false);
    const [pipelineError, setPipelineError] = useState<string | null>(null);
    const [chunksIndexed, setChunksIndexed] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Poll run status
    useEffect(() => {
        if (!runId) return;

        const poll = async () => {
            try {
                const data = await api.getRunStatus(runId);
                setSteps(data.steps || []);
                setCurrentStep(data.current_step || 'queued');

                setChunksIndexed(data.chunks_indexed || 0);

                if (data.current_step === 'done' || data.current_step === 'completed') {
                    setPipelineDone(true);
                    if (pollRef.current) clearInterval(pollRef.current);
                } else if (data.current_step === 'error') {
                    setPipelineError(data.current_detail);
                    if (pollRef.current) clearInterval(pollRef.current);
                }
            } catch {
                // Ignore poll errors
            }
        };

        pollRef.current = setInterval(poll, 2000);
        poll(); // immediate first check

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [runId]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && (file.type === 'application/pdf' || file.name.endsWith('.pdf') || file.name.endsWith('.docx'))) {
            setSelectedFile(file);
            resetPipeline();
        }
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            resetPipeline();
        }
    };

    const resetPipeline = () => {
        setRunId(null);
        setSteps([]);
        setCurrentStep('');

        setPipelineDone(false);
        setPipelineError(null);
        setChunksIndexed(0);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        resetPipeline();

        try {
            const response = await api.uploadPdf(selectedFile, projectId, entityId);
            const newRunId = response.run_id;
            setRunId(newRunId);
            setCurrentStep('queued');

            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
            setPipelineError(err.response?.data?.detail || err.message || 'Error desconocido');
        } finally {
            setUploading(false);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    const isStepComplete = (stepName: string) => {
        const stepOrder = ['extracting', 'chunking', 'embedding', 'indexing', 'completed', 'post_processing', 'done'];
        const currentIdx = stepOrder.indexOf(currentStep);
        const stepIdx = stepOrder.indexOf(stepName);
        return stepIdx < currentIdx;
    };

    const isStepActive = (stepName: string) => currentStep === stepName;

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-white mb-1">Ingesta de Documentos</h3>
                <p className="text-sm text-slate-400">
                    Sube un PDF o DOCX para agregar información a la entidad <span className="text-primary font-medium">{entityId}</span>.
                    El documento se analizará con IA y se indexará directamente en esta entidad.
                </p>
            </div>

            {/* Drop Zone */}
            {!runId && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                        relative cursor-pointer border-2 border-dashed rounded-xl p-10 text-center
                        transition-all duration-300 ease-in-out
                        ${isDragOver
                            ? 'border-primary bg-primary/10 scale-[1.01]'
                            : 'border-slate-700 hover:border-slate-500 hover:bg-slate-800/30'
                        }
                    `}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors ${isDragOver ? 'bg-primary/20' : 'bg-slate-800'}`}>
                        <Upload className={`w-7 h-7 ${isDragOver ? 'text-primary' : 'text-slate-400'}`} />
                    </div>

                    <p className="text-slate-300 font-medium mb-1">
                        {isDragOver ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para seleccionar'}
                    </p>
                    <p className="text-xs text-slate-500">
                        Formatos aceptados: PDF, DOCX • Tamaño máximo: 50 MB
                    </p>
                </div>
            )}

            {/* Selected File Preview */}
            {selectedFile && !runId && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                            <p className="text-sm text-white font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-slate-400">{formatSize(selectedFile.size)}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded border border-primary/20">
                            → {entityId}
                        </span>
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Iniciando...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4" />
                                    Subir e Ingestar
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Pipeline Progress Stepper */}
            {runId && (
                <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        {pipelineDone ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : pipelineError ? (
                            <AlertCircle className="w-5 h-5 text-red-400" />
                        ) : (
                            <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        )}
                        Pipeline de Ingesta Semántica
                    </h4>

                    <div className="space-y-3">
                        {['extracting', 'chunking', 'embedding', 'indexing', 'post_processing', 'done'].map((stepName) => {
                            const config = STEP_CONFIG[stepName] || STEP_CONFIG.queued;
                            const Icon = config.icon;
                            const complete = isStepComplete(stepName);
                            const active = isStepActive(stepName);
                            const stepData = steps.find(s => s.step === stepName);

                            return (
                                <div key={stepName} className={`flex items-start gap-3 py-2 px-3 rounded-lg transition-all duration-300 ${
                                    active ? 'bg-slate-700/50 border border-slate-600' : 
                                    complete ? 'opacity-70' : 'opacity-30'
                                }`}>
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                        complete ? 'bg-green-500/20' : active ? 'bg-slate-600' : 'bg-slate-800'
                                    }`}>
                                        {complete ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                                        ) : active ? (
                                            <Icon className={`w-4 h-4 ${config.color} ${active ? 'animate-pulse' : ''}`} />
                                        ) : (
                                            <Icon className="w-4 h-4 text-slate-500" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium ${complete || active ? 'text-white' : 'text-slate-500'}`}>
                                            {config.label}
                                        </p>
                                        {(active || complete) && stepData && (
                                            <p className="text-xs text-slate-400 mt-0.5 truncate">
                                                {stepData.detail}
                                            </p>
                                        )}
                                    </div>
                                    {active && !complete && (
                                        <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0 mt-1" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Result summary */}
                    {pipelineDone && (
                        <div className="mt-4 p-3 bg-green-500/5 border border-green-500/20 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                            <div>
                                <p className="text-sm text-green-300 font-medium">
                                    ✅ Ingesta completada — {chunksIndexed} chunks semánticos indexados
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Verifica los resultados en Documents (Facts)
                                </p>
                            </div>
                        </div>
                    )}

                    {pipelineError && (
                        <div className="mt-4 p-3 bg-red-500/5 border border-red-500/20 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                            <p className="text-sm text-red-300">{pipelineError}</p>
                        </div>
                    )}

                    {(pipelineDone || pipelineError) && (
                        <button
                            onClick={() => { resetPipeline(); }}
                            className="mt-3 text-xs text-primary hover:text-blue-300 transition-colors"
                        >
                            ← Subir otro documento
                        </button>
                    )}
                </div>
            )}

            {/* Info Box */}
            {!runId && (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Pipeline de Ingesta Semántica</h4>
                    <ol className="text-xs text-slate-500 space-y-1.5 list-decimal list-inside">
                        <li>Se extrae el texto del documento (OCR o pdfplumber)</li>
                        <li><span className="text-amber-400">GPT-4o-mini</span> analiza y divide en <span className="text-slate-300">chunks semánticos</span> (1 CA = 1 chunk, 1 Regla = 1 chunk)</li>
                        <li>Cada chunk se vectoriza con <span className="text-slate-300">OpenAI Embeddings</span> y se indexa en Qdrant</li>
                        <li>Los chunks se asignan a <span className="text-primary">{entityId}</span> con metadata enriquecida</li>
                        <li>Verifica los resultados en <span className="text-slate-300">Documents (Facts)</span></li>
                    </ol>
                </div>
            )}
        </div>
    );
};

export default EntityUploadTab;
